import * as React from "react"
import type { ConsumerInfoType, PaginationType } from "@/types"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import type { DateRange } from "react-day-picker"

import { ServiceRequestHistoryType } from "@/types/service-request-history"
import { getConsumer } from "@/lib/api/billing"
import { getServiceRequestHistory } from "@/lib/api/service-request"
import { getFullName } from "@/lib/auth"
import { DataTable } from "@/components/data-table"
import { DateRangePicker } from "@/components/date-range-picker"
import { Header } from "@/components/header"
import { serviceRequestHistoryColumns } from "@/components/table/columns/service-request-history-column"

export const Route = createFileRoute(
  "/_home/manage-consumers/view-service-request-history/$id",
)({
  loader: async ({ params: { id } }) => {
    return { id }
  },
  component: ViewConsumerServiceRequestHistoryPage,
})

function ViewConsumerServiceRequestHistoryPage() {
  const { id } = Route.useLoaderData()
  const [date, setDate] = React.useState<DateRange>({
    from: new Date(new Date().setDate(new Date().getDate() - 7)), // 7 days ago
    to: new Date(), // Today
  })
  const [defaultPage, setDefaultPage] = React.useState<number>(2)
  const [page, setPage] = React.useState<number>(1)
  const [search, setSearch] = React.useState<string>("")

  const [nextPageUrl, setNextPageUrl] = React.useState<string | null>(null)
  const [prevPageUrl, setPrevPageUrl] = React.useState<string | null>(null)
  const fullName = getFullName()
  const { data: consumerInfo } = useQuery<ConsumerInfoType>({
    queryKey: ["consumerInfo", id],
    queryFn: async () => {
      const consumer = await getConsumer(Number(id))
      return { ...consumer, id }
    },
  })
  const {
    data: serviceRequestHistory,
    isLoading,
    isError,
  } = useQuery<PaginationType<ServiceRequestHistoryType>>({
    queryKey: [
      "serviceRequestHistoryConsumer",
      date,
      id,
      defaultPage,
      page,
      search,
    ],
    queryFn: async () => {
      try {
        const from = date.from ?? new Date()
        const to = date.to ?? new Date()
        const serviceRequestHistory = await getServiceRequestHistory(
          Number(id),
          from.toISOString(),
          to.toISOString(),
          defaultPage,
          page,
          search,
        )
        return serviceRequestHistory
      } catch (error) {
        console.error("Error fetching service request history:", error)
        return {
          count: 0,
          next: null,
          previous: null,
          results: [], // ✅ Ensure an empty results array instead of []
        }
      }
    },
    retry: false, // Prevent retrying if there's an error
    staleTime: 1000 * 60 * 5, // Cache data for 5 minutes
  })

  React.useEffect(() => {
    if (serviceRequestHistory) {
      setNextPageUrl(serviceRequestHistory.next)
      setPrevPageUrl(serviceRequestHistory.previous)
    }
  }, [serviceRequestHistory])
  return (
    <main className="space-y-4 p-4">
      <Header
        subroutes={[
          {
            route: "/manage-consumers",
            label: "Service Request History",
          },
        ]}
      >
        {`Service Request History (${consumerInfo?.full_name})` || "N/A"}
      </Header>
      {isError ? (
        <p className="text-center text-red-500">
          Failed to load billing records.
        </p>
      ) : (
        <DataTable
          columns={serviceRequestHistoryColumns}
          data={serviceRequestHistory?.results ?? []}
          search={{
            // field: "name",
            label: "Name",
            value: search,
            onChange: setSearch,
          }}
          isLoading={isLoading}
          isError={isError}
          defaultPage={defaultPage}
          setDefaultPage={setDefaultPage}
          nextPageUrl={nextPageUrl}
          prevPageUrl={prevPageUrl}
          page={page}
          setPage={setPage}
          totalCount={serviceRequestHistory?.count}
        >
          <div className="flex w-auto justify-end md:w-full">
            <DateRangePicker date={date} setDate={setDate} />
          </div>
        </DataTable>
      )}
    </main>
  )
}
